// =============================================================================
// src/lib/org.ts
// Hooks + write helpers for the org management surface.
//
//   useOrgMembers(orgId) — members + each member's role + email/name
//   useOrgInvites(orgId) — pending invites (not accepted, not revoked, unexpired)
//
//   updateOrgName(orgId, name)            — owner/admin can rename
//   updateMemberRole(orgId, userId, role) — owner/admin (trigger blocks last-owner demote)
//   removeMember(orgId, userId)           — owner/admin (trigger blocks last-owner)
//   sendInvite(orgId, email, role)        — calls org-invite Edge Function (creates row + emails)
//   revokeInvite(inviteId)                — calls org_invite_revoke RPC
//   deleteOrg(orgId)                       — owner only
// =============================================================================

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

// Re-export hook + helpers below. The user_profiles UPDATE grant
// (column-scoped to active_organization_id) is what makes
// switchActiveOrg() work without an RPC.

export interface OrgMember {
  organization_id: string
  user_id: string
  role: 'owner' | 'admin' | 'member'
  joined_at: string
  email: string | null
  first_name: string | null
  last_name: string | null
  display_name: string
  last_sign_in_at: string | null
}

export interface OrgInvite {
  id: string
  organization_id: string
  email: string
  role: 'admin' | 'member'
  token: string
  created_by: string | null
  created_at: string
  expires_at: string
  accepted_at: string | null
  accepted_by: string | null
  revoked_at: string | null
}

// ---- members ----------------------------------------------------------------

interface UseOrgMembersResult {
  members: OrgMember[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useOrgMembers(orgId: string | null): UseOrgMembersResult {
  const [members, setMembers] = useState<OrgMember[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!orgId) {
      setMembers([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    // org_members_admin is a SECURITY DEFINER RPC: owner/admins get real
    // names + emails + last sign-in (resolved server-side from auth.users,
    // which RLS won't let the client read). Non-admins are rejected by the
    // RPC's role gate — fine, since the Organization page hides the member
    // list from them anyway. On any error we return an empty list.
    const { data, error: rpcErr } = await supabase.rpc('org_members_admin', { p_org_id: orgId })
    if (rpcErr) {
      setMembers([])
      setError(null)
      setLoading(false)
      return
    }
    type Row = {
      user_id: string
      display_name: string
      first_name: string | null
      last_name: string | null
      email: string | null
      role: OrgMember['role']
      joined_at: string
      last_sign_in_at: string | null
    }
    const enriched: OrgMember[] = ((data ?? []) as Row[]).map((r) => ({
      organization_id: orgId,
      user_id: r.user_id,
      role: r.role,
      joined_at: r.joined_at,
      email: r.email,
      first_name: r.first_name,
      last_name: r.last_name,
      display_name: r.display_name,
      last_sign_in_at: r.last_sign_in_at,
    }))
    setMembers(enriched)
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { members, loading, error, refresh: load }
}

// ---- invites ----------------------------------------------------------------

interface UseOrgInvitesResult {
  invites: OrgInvite[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useOrgInvites(orgId: string | null): UseOrgInvitesResult {
  const [invites, setInvites] = useState<OrgInvite[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!orgId) {
      setInvites([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('organization_invites')
      .select('*')
      .eq('organization_id', orgId)
      .is('accepted_at', null)
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
    if (err) {
      setError(err.message)
      setInvites([])
    } else {
      setInvites((data ?? []) as OrgInvite[])
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { invites, loading, error, refresh: load }
}

// ---- writes -----------------------------------------------------------------

export async function updateOrgName(orgId: string, name: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('organizations')
    .update({ name: name.trim() })
    .eq('id', orgId)
  return { error: error?.message ?? null }
}

export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: 'admin' | 'member' | 'owner',
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('organization_members')
    .update({ role })
    .eq('organization_id', orgId)
    .eq('user_id', userId)
  return { error: error?.message ?? null }
}

export async function removeMember(
  orgId: string,
  userId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('organization_id', orgId)
    .eq('user_id', userId)
  return { error: error?.message ?? null }
}

/**
 * Send an invite. Calls the org-invite Edge Function which creates the
 * invite row + sends the branded email via Resend. Returns the new invite
 * id on success.
 */
export async function sendInvite(
  orgId: string,
  email: string,
  role: 'admin' | 'member',
): Promise<{ inviteId: string | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke('org-invite', {
    body: { org_id: orgId, email, role },
  })
  if (error) return { inviteId: null, error: error.message }
  const body = data as { ok?: boolean; invite_id?: string; error?: string } | null
  if (!body?.ok) return { inviteId: null, error: body?.error ?? 'Invite failed.' }
  return { inviteId: body.invite_id ?? null, error: null }
}

export async function revokeInvite(inviteId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('org_invite_revoke', { p_invite_id: inviteId })
  return { error: error?.message ?? null }
}

/**
 * Resend a pending invite. Passes the invite's CURRENT role, so
 * org_invite_create takes its same-role refresh path: it keeps the existing
 * token (the already-delivered link stays valid) and just refreshes the expiry
 * before re-sending. There's no revoke-then-fail gap, and a failed send can't
 * invalidate the prior link. (Changing an invite's role requires revoke +
 * re-invite, which mints a fresh token + email.)
 */
export async function resendInvite(
  orgId: string,
  invite: { id: string; email: string; role: 'admin' | 'member' },
): Promise<{ error: string | null }> {
  const { error } = await sendInvite(orgId, invite.email, invite.role)
  return { error }
}

export async function deleteOrg(orgId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('organizations').delete().eq('id', orgId)
  return { error: error?.message ?? null }
}

/**
 * Create a new organization owned by the caller. Server-side RPC handles
 * org row + owner membership + switching active_organization_id in one
 * atomic transaction. Returns the new org row.
 */
export async function createOrg(
  name: string,
): Promise<{ org: { id: string; name: string; slug: string } | null; error: string | null }> {
  const { data, error } = await supabase.rpc('org_create', { p_name: name.trim() })
  if (error) return { org: null, error: error.message }
  const row = (Array.isArray(data) ? data[0] : data) as { id: string; name: string; slug: string } | null
  return { org: row, error: null }
}

/**
 * Switch the caller's active org. Backed by the user_profiles UPDATE
 * grant (active_organization_id is in the safe-columns list). The
 * caller must be a member of the target org — RLS on api_keys + usage
 * checks org membership, so an invalid switch surfaces as empty
 * dashboards rather than 4xx.
 */
export async function switchActiveOrg(
  userId: string,
  orgId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ active_organization_id: orgId })
    .eq('user_id', userId)
  return { error: error?.message ?? null }
}

export interface UserOrgRow {
  organization_id: string
  name: string
  slug: string
  role: 'owner' | 'admin' | 'member'
}

/**
 * List every org the current user belongs to, with their role in each.
 * Used by the AccountOrgPage's "Your organizations" switcher.
 */
export function useUserOrgs() {
  const [orgs, setOrgs] = useState<UserOrgRow[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data: members, error: err } = await supabase
      .from('organization_members')
      .select('organization_id, role, organizations(id, name, slug)')
      .order('joined_at', { ascending: true })
    if (err) {
      setError(err.message)
      setOrgs([])
      setLoading(false)
      return
    }
    // Type the embedded join (Supabase JS returns it as an object or array
    // depending on FK config; defensive narrowing keeps it simple).
    type Row = {
      organization_id: string
      role: 'owner' | 'admin' | 'member'
      organizations: { id: string; name: string; slug: string } | { id: string; name: string; slug: string }[] | null
    }
    const flattened: UserOrgRow[] = (members ?? [] as unknown as Row[]).flatMap((m) => {
      const row = m as Row
      const orgInfo = Array.isArray(row.organizations) ? row.organizations[0] : row.organizations
      if (!orgInfo) return []
      return [{
        organization_id: row.organization_id,
        role: row.role,
        name: orgInfo.name,
        slug: orgInfo.slug,
      }]
    })
    setOrgs(flattened)
    setLoading(false)
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { orgs, loading, error, refresh: load }
}

/**
 * Accept an invite by token. Used by the /account/org/invite/:token landing
 * page after the recipient signs in. Idempotent — re-accepting an already-
 * accepted invite returns the same row.
 */
export async function acceptInvite(
  token: string,
): Promise<{ inviteOrgId: string | null; error: string | null }> {
  const { data, error } = await supabase.rpc('org_invite_accept', { p_token: token })
  if (error) return { inviteOrgId: null, error: error.message }
  const row = (Array.isArray(data) ? data[0] : data) as { organization_id?: string } | null
  return { inviteOrgId: row?.organization_id ?? null, error: null }
}

/**
 * Public lookup of an invite by token. Renders the "you've been invited to
 * X by Y" landing before the user signs in.
 */
export interface PublicInviteInfo {
  organization_id: string
  organization_name: string
  email: string
  role: 'admin' | 'member'
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
  inviter_first_name: string | null
}

export async function lookupInvitePublic(
  token: string,
): Promise<{ invite: PublicInviteInfo | null; error: string | null }> {
  const { data, error } = await supabase.rpc('org_invite_lookup_public', { p_token: token })
  if (error) return { invite: null, error: error.message }
  const row = Array.isArray(data) ? data[0] : null
  return { invite: (row as PublicInviteInfo | null) ?? null, error: null }
}

// ---- member directory (names only, any member) ------------------------------

/**
 * Map of user_id -> display name for every member of an org. Backed by the
 * org_member_directory RPC (member-callable, names only — no email). Used to
 * attribute API keys (created_by) to a person without exposing emails.
 */
export function useOrgMemberDirectory(orgId: string | null) {
  const [names, setNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<boolean>(true)

  const load = useCallback(async () => {
    if (!orgId) {
      setNames({})
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await supabase.rpc('org_member_directory', { p_org_id: orgId })
    if (error) {
      setNames({})
      setLoading(false)
      return
    }
    const map: Record<string, string> = {}
    for (const r of (data ?? []) as Array<{ user_id: string; display_name: string }>) {
      map[r.user_id] = r.display_name
    }
    setNames(map)
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { names, loading, refresh: load }
}
